import type { Bet365SelectionData, BetMGMSelectionData, DraftKingsSelectionData, FanDuelSelectionData } from '../lib/api-client'
import type { Sportsbook } from '@/types/types'
import {
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useRef, useState } from 'react'
import { useEvents } from '../hooks/useEvents'
import { trackMarketChange, trackOddsClick, trackWidgetView } from '../lib/analytics'
import { globalBetSlipManager } from './GlobalBetSlipManager'
import { useUIPreferences } from './UIPreferencesManager'
import Widget from './Widget'

// Transformed selection data types (for bet slip - all strings)
type TransformedSelectionData
  = | { marketId: string, selectionId: string } // FanDuel
    | { selectionId: string } // DraftKings/Bet365
    | { optionId: string, marketId: string, fixtureId: string } // BetMGM

interface AppProps {
  instanceId: string
  league: string
}

function App({
  instanceId,
  league,
}: AppProps) {
  const { data: response, isPending, error } = useEvents(league)
  const { oddsFormat, toggleOddsFormat } = useUIPreferences()
  const [selectedMarketIndex, setSelectedMarketIndex] = useState<number>(0)
  const hasTrackedView = useRef(false)

  const handleMarketChange = (marketIndex: number) => {
    setSelectedMarketIndex(marketIndex)
    const market = response?.data?.[marketIndex]
    if (market) {
      trackMarketChange(league, market.marketName, market.name)
    }
  }

  if (isPending)
    return <div>Loading...</div>
  if (error) {
    return (
      <div>
        Error:
        {error.message}
      </div>
    )
  }

  if (!response || !response.data || response.data.length === 0)
    return null

  // Register sportsbook links for this league so the bet slip can use them
  if (response.sportsbookLinks) {
    globalBetSlipManager.setSportsbookLinks(league, response.sportsbookLinks)
  }

  const events = response.data
  const h2hStartIndex = response.h2hStartIndex

  // Build H2H indices from the backend-provided start index
  const h2hIndices = h2hStartIndex != null
    ? events.map((_, i) => i).filter(i => i >= h2hStartIndex)
    : []

  // If ALL markets are H2H (multiEvent sport like PFL), it's pure H2H mode
  // If backend provided h2hStartIndex, it's hybrid mode
  const isH2HMode = h2hIndices.length > 0 && h2hStartIndex === 0
  const hasH2H = h2hIndices.length > 0

  if (!hasTrackedView.current) {
    hasTrackedView.current = true
    trackWidgetView(league)
  }

  const handleSelectionClick = (marketIndex: number, selectionIndex: number, sportsbook: Sportsbook) => {
    const event = events[marketIndex]
    if (!event?.selections?.length || !instanceId) {
      console.warn('Missing required data for bet selection')
      return
    }

    const selection = event.selections[selectionIndex]
    if (!selection) {
      console.warn(`Selection at index ${selectionIndex} not found`)
      return
    }

    const oddsData = selection.odds[sportsbook]
    if (!oddsData) {
      console.warn(`No odds data found for ${sportsbook}`)
      return
    }

    // Convert selectedData to ensure string types for compatibility
    let selectedData: TransformedSelectionData

    // Handle different sportsbook data structures
    if (sportsbook === 'FanDuel' && oddsData.selectionData) {
      const fanDuelData = oddsData.selectionData as FanDuelSelectionData
      selectedData = {
        marketId: fanDuelData.marketId,
        selectionId: fanDuelData.selectionId.toString(),
      }
    }
    else if (sportsbook === 'DraftKings' && oddsData.selectionData) {
      const draftKingsData = oddsData.selectionData as DraftKingsSelectionData
      selectedData = {
        selectionId: draftKingsData.selectionId.toString(),
      }
    }
    else if (sportsbook === 'Bet365' && oddsData.selectionData) {
      const bet365Data = oddsData.selectionData as Bet365SelectionData
      selectedData = {
        selectionId: bet365Data.selectionId.toString(),
      }
    }
    else if (sportsbook === 'BetMGM' && oddsData.selectionData) {
      const betMGMData = oddsData.selectionData as BetMGMSelectionData
      selectedData = {
        optionId: betMGMData.optionId.toString(),
        marketId: betMGMData.marketId.toString(),
        fixtureId: betMGMData.fixtureId.toString(),
      }
    }
    else {
      // Default empty object if no selectionData available
      selectedData = { selectionId: '' }
    }

    const betData = {
      league,
      eventName: event.name,
      marketName: event.marketName,
      sportsbook,
      selectionName: selection.name,
      odds: {
        american: oddsData.american,
        decimal: oddsData.decimal,
      },
      selectedData,
      stake: '',
    }

    trackOddsClick(league, sportsbook, selection.name, event.marketName, event.name)
    globalBetSlipManager.addBet(instanceId, betData)
  }

  return (
    <Widget
      leagueName={response.leagueName}
      markets={events}
      selectedMarketIndex={selectedMarketIndex}
      onMarketChange={handleMarketChange}
      oddsFormat={oddsFormat}
      onToggleOddsFormat={toggleOddsFormat}
      onSelectionClick={handleSelectionClick}
      isH2HMode={isH2HMode}
      h2hIndices={hasH2H ? h2hIndices : undefined}
      eventName={h2hStartIndex != null && h2hStartIndex > 0 ? events[0]!.name : undefined}
    />
  )
}

const queryClient = new QueryClient()

export default function AppWidget(props: AppProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <App {...props} />
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}
